import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getStationeryDamageDispatchById } from 'src/redux/slices/stationery-damage-dispatch';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';
import StationeryDamageDispatchDetails from '../stationery-damage-dispatch-details';

type Props = {
  id: string;
};

export default function StationeryDamageDispatchDetailView({ id }: Props) {
  const {
    damageDispatch: currentDamageDispatch,
    status,
    error,
  } = useAppSelector((state) => state.stationeryDamageDispatch);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getStationeryDamageDispatchById({ itemID: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load damage dispatch details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.stationeryDamageDispatch.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Damage Dispatch
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  if (status === 'success' && currentDamageDispatch) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Damage Dispatch Details"
          links={[
            {
              name: 'Damage Dispatch',
              href: paths.dashboard.stationeryDamageDispatch.root,
            },
            { name: 'Damage Dispatch Details' },
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <StationeryDamageDispatchDetails currentDamageDispatch={currentDamageDispatch} />
      </Container>
    );
  }

  return <span />;
}
